// Resizes a photo the manager uploads into the image Facebook and WhatsApp will show.
//
// Neither file we already hold works: the _300x300 thumbnail is 300x169 for a 16:9
// source, under the 200px floor both platforms enforce, and the full-size original can
// exceed the ~300KB above which WhatsApp stops rendering a preview. 1200x675 under
// 300KB satisfies both.
//
// The file comes from the manager's own device, so it is same-origin and the canvas is
// never tainted - no storage-bucket CORS configuration is involved.

const WIDTH_STEPS = [1200, 1000, 800];  // 1200 is Facebook's recommended width
const MAX_BYTES = 307200;      // 300 KB - WhatsApp drops the preview above roughly this
const MIN_SIDE = 200;          // both platforms discard anything smaller
const QUALITY_STEPS = [0.75, 0.65, 0.55];  // 0.75 measured at 274 KB for a 1280x720 source

function loadLocalImage(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('could not read the image file'));
    };
    img.src = objectUrl;
  });
}

function toBlob(canvas, quality) {
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
}

// Resolves to { ok: true, blob, width, height, bytes } or { ok: false, reason }.
export async function buildShareImageFromFile(file) {
  let image;
  try {
    image = await loadLocalImage(file);
  } catch (error) {
    return { ok: false, reason: 'unreadable' };
  }

  const sourceWidth = image.naturalWidth;
  const sourceHeight = image.naturalHeight;
  if (sourceWidth < MIN_SIDE || sourceHeight < MIN_SIDE) {
    return { ok: false, reason: 'tooSmall', width: sourceWidth, height: sourceHeight };
  }

  // Only ever shrink - upscaling adds no detail and would just inflate the file.
  // A tall photo (4:3 rather than 16:9) carries more pixels at the same width, so the
  // quality ladder alone can miss the budget; fall back to narrower widths after it.
  let best = null;
  for (let w = 0; w < WIDTH_STEPS.length; w += 1) {
    const scale = Math.min(1, WIDTH_STEPS[w] / sourceWidth);
    const width = Math.round(sourceWidth * scale);
    const height = Math.round(sourceHeight * scale);
    if (height < MIN_SIDE || width < MIN_SIDE) break;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(image, 0, 0, width, height);

    for (let q = 0; q < QUALITY_STEPS.length; q += 1) {
      // eslint-disable-next-line no-await-in-loop
      const blob = await toBlob(canvas, QUALITY_STEPS[q]);
      if (!blob) continue;
      if (blob.size <= MAX_BYTES) {
        return { ok: true, blob, width, height, bytes: blob.size };
      }
      if (!best || blob.size < best.bytes) {
        best = { ok: true, blob, width, height, bytes: blob.size };
      }
    }
  }

  // Nothing fit the budget - hand back the smallest result rather than nothing.
  if (best) return best;
  return { ok: false, reason: 'unreadable' };
}
